import type { ProjectRepository } from '@/domain/project/ProjectRepository'
import type { ProjectMetadata } from '@/domain/project/types'

/**
 * Looks up an existing project whose `name` matches `name` exactly.
 *
 * Used by the home/library upload flow to detect filename collisions
 * with existing projects: when a match exists, the UI offers the user
 * the choice between importing the file as a new version of the
 * existing project or creating a new project that happens to share
 * the name.
 *
 * If multiple projects share the same name (the rename flow doesn't
 * prevent it), the most recently updated one is returned — that's
 * the one the user is most likely to mean.
 */
export async function findProjectByName(
  name: string,
  repo: ProjectRepository,
): Promise<ProjectMetadata | null> {
  const projects = await repo.listProjects()
  const matches = projects.filter((p) => p.name === name)
  if (matches.length === 0) return null
  // listProjects returns them sorted by updatedAt desc, so matches[0]
  // is already the most-recent one. Be defensive anyway.
  matches.sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1))
  return matches[0]
}